import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ViewIcon } from '../../assets/icons/background-eye-icon.svg';
import { ReactComponent as PdfIcon } from '../../assets/icons/export-pdf.svg';
import { ReactComponent as TransactionIcon } from '../../assets/icons/sidebar/sidebar-payment.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import { InvoiceInterface, RequestInvoiceInterface } from '../../interface/invoice-interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchInvoiceList,
  fetchInvoiceFilters,
  setSortData,
  updateInvoiceListFilter,
} from '../../redux/slices/InvoiceSlice';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import SidePaneModel from '../../shared-components/table-side-panel/SidePaneModel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoDateTime from '../../shared-functions/ConvertUTCtoDateTime';
import { convertUtcTo12HourTime } from '../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import FormatDuration from '../../shared-functions/FormatDuration';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import invoiceApi from '../../utils/api/invoice';

import InvoiceSidePanel from './components/InvoiceSidePanel';
import downloadSingleInvoicePdf from './utils/generateInvoicePdf';

/**
 * Properties for EmspInvoice component
 */
interface EmspInvoiceProperties {
  userCustomId?: string;
  isComponent?: boolean;
}

/**
 * Helper to convert duration string (e.g. "HH:MM:SS" or seconds) to total seconds.
 * @param {string | number} [duration] - The duration value.
 * @returns {number} The total seconds.
 */
const parseDurationToSeconds = (duration?: string | number): number => {
  if (!duration) return 0;
  if (typeof duration === 'number') return duration;
  if (typeof duration === 'string' && duration.includes(':')) {
    const parts = duration.split(':').map((p) => Number(p) || 0);
    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    }
    if (parts.length === 2) {
      return parts[0] * 60 + parts[1];
    }
  }
  const numericValue = Number(duration);
  return !Number.isNaN(numericValue) ? numericValue : 0;
};

/**
 * Component to display the invoice list for EMSP
 * @param {EmspInvoiceProperties} [properties] properties
 * @returns {any} The result
 */
function EmspInvoice(properties: EmspInvoiceProperties = {}) {
  const { userCustomId, isComponent } = properties;
  const { t, i18n } = useTranslation('invoice');
  const dispatch = useAppDispatch();

  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceInterface | null>(null);
  const [isInvoicePanelOpen, setIsInvoicePanelOpen] = useState(false);

  const {
    invoices,
    loading,
    page,
    limit,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    sortField,
    sortBy,
    isFilterUpdated,
  } = useAppSelector((state) => state.Invoice);

  /**
   * Fetch Invoice List
   * @param {RequestInvoiceInterface} data data
   */
  const fetchData = (data: RequestInvoiceInterface) => {
    if (!loading) {
      const payload: any = { ...data };
      if (userCustomId) {
        payload.userCustomId = userCustomId;
      }
      delete payload.sortField; // Remove sortField as backend does not allow it
      delete payload.sortBy; // Remove sortBy to avoid further validation errors
      dispatch(fetchInvoiceList(payload));
    }
  };

  /**
   * Table Columns
   */
  const columns = [
    {
      label: t('invoiceNumber'),
      accessorKey: 'invoiceNumber' as keyof InvoiceInterface,
      sortAllow: true,
    },
    {
      label: t('invoiceDate'),
      accessorKey: 'invoiceDate' as keyof InvoiceInterface,
      sortAllow: true,
      /**
       * Custom Data for invoiceDate
       * @param {InvoiceInterface} currentRow currentRow
       * @returns {any} HTML data
       */
      customData: (currentRow: InvoiceInterface) => (
        <span>{ConvertUTCtoDateTime(currentRow.invoiceDate)}</span>
      ),
    },
    {
      label: t('customerName'),
      accessorKey: 'invoiceTo' as keyof InvoiceInterface,
      sortAllow: false,
      /**
       * Custom Data for customerName
       * @param {InvoiceInterface} currentRow currentRow
       * @returns {any} HTML data
       */
      customData: (currentRow: InvoiceInterface) => <p>{currentRow.invoiceTo?.name || '-'}</p>,
    },
    {
      label: t('userId'),
      accessorKey: 'userCustomId' as keyof InvoiceInterface,
      sortAllow: true,
    },
    {
      label: t('sessionId'),
      accessorKey: 'sessionId' as keyof InvoiceInterface,
      sortAllow: false,
    },
    {
      label: t('transactionId'),
      accessorKey: 'transactionId' as keyof InvoiceInterface,
      sortAllow: false,
    },
    {
      label: t('stationId'),
      accessorKey: 'chargerInfo' as keyof InvoiceInterface,
      sortAllow: false,
      /**
       * Custom Data for stationId
       * @param {InvoiceInterface} currentRow currentRow
       * @returns {any} HTML data
       */
      customData: (currentRow: InvoiceInterface) => (
        <p>{currentRow.chargerInfo?.stationId || '-'}</p>
      ),
    },
    {
      label: t('chargerId'),
      accessorKey: 'chargerInfo' as keyof InvoiceInterface,
      sortAllow: false,
      /**
       * Custom Data for chargerId
       * @param {InvoiceInterface} currentRow currentRow
       * @returns {any} HTML data
       */
      customData: (currentRow: InvoiceInterface) => (
        <p>{currentRow.chargerInfo?.chargerId || '-'}</p>
      ),
    },
    {
      label: t('energyUsed'),
      accessorKey: 'chargingDetails' as keyof InvoiceInterface,
      sortAllow: false,
      /**
       * Custom Data for energyUsed
       * @param {InvoiceInterface} currentRow currentRow
       * @returns {any} HTML data
       */
      customData: (currentRow: InvoiceInterface) => (
        <p>{`${Number(currentRow.chargingDetails?.energyUsed || 0).toFixed(3)} kWh`}</p>
      ),
    },
    {
      label: t('duration'),
      accessorKey: 'chargingDetails' as keyof InvoiceInterface,
      sortAllow: false,
      /**
       * Custom Data for duration
       * @param {InvoiceInterface} currentRow currentRow
       * @returns {any} HTML data
       */
      customData: (currentRow: InvoiceInterface) => {
        const seconds = parseDurationToSeconds(currentRow.chargingDetails?.chargingDuration);
        return <p>{FormatDuration(seconds, '-')}</p>;
      },
    },
    {
      label: t('amount'),
      accessorKey: 'chargingDetails' as keyof InvoiceInterface,
      sortAllow: false,
      /**
       * Custom Data for amount
       * @param {InvoiceInterface} currentRow currentRow
       * @returns {any} HTML data
       */
      customData: (currentRow: InvoiceInterface) => (
        <p>
          {ToFixedValue(currentRow.chargingDetails?.invoiceAmountPaid || 0)} {currentRow.currency}
        </p>
      ),
    },
    {
      label: t('action'),
      accessorKey: 'action' as keyof InvoiceInterface,
      sortAllow: false,
      ColumnWidth: 160,
      hideWhenSidePanelOpen: true,
      sticky: 'right' as const,
      /**
       * Custom Data for action
       * @param {InvoiceInterface} currentRow currentRow
       * @returns {any} HTML data
       */
      customData: (currentRow: InvoiceInterface) => (
        <div className="table-action-icons">
          <button
            type="button"
            onClick={() => {
              setSelectedInvoice(currentRow);
              setIsInvoicePanelOpen(true);
            }}
            className="table-icon-button eye-icon-wrapper">
            <ViewIcon />
          </button>

          <button
            type="button"
            onClick={() => {
              downloadSingleInvoicePdf(currentRow);
            }}
            className="table-icon-button pdf-icon-wrapper">
            <PdfIcon />
          </button>
        </div>
      ),
    },
  ];

  /**
   * Current Columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('invoiceNumber'), accessorKey: 'invoiceNumber' },
    { label: t('invoiceDate'), accessorKey: 'invoiceDate' },
    { label: t('customerName'), accessorKey: 'customerName' },
    { label: t('userId'), accessorKey: 'userCustomId' },
    { label: t('sessionId'), accessorKey: 'sessionId' },
    { label: t('transactionId'), accessorKey: 'transactionId' },
    { label: t('stationId'), accessorKey: 'stationId' },
    { label: t('chargerId'), accessorKey: 'chargerId' },
    { label: t('energyUsed'), accessorKey: 'energyUsed' },
    { label: t('duration'), accessorKey: 'chargingDuration' },
    { label: t('amount'), accessorKey: 'invoiceAmountPaid' },
    { label: t('created', 'Created'), accessorKey: 'createdAt' },
    { label: t('lastUpdated', 'Last Updated'), accessorKey: 'updatedAt' },
  ];

  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param type - The export format type ('pdf' or 'csv').
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);

    const payload: any = {
      ...queryParameters,
    };

    if (userCustomId) payload.userCustomId = userCustomId;

    if (sortField) payload.sortField = sortField;
    if (sortBy) payload.sortBy = sortBy;

    invoiceApi
      .invoiceExport(payload)
      .then((response) => {
        const headers = exportCurrentColumns.map((column) => column.label);

        const rows = response.data.map((row: InvoiceInterface, index: number) =>
          exportCurrentColumns.map((column) => {
            switch (column.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'invoiceNumber':
                return row.invoiceNumber || '-';

              case 'invoiceDate':
                return row.invoiceDate
                  ? `${ConvertUTCtoDate(row.invoiceDate)} ${convertUtcTo12HourTime(row.invoiceDate)}`
                  : '-';

              case 'customerName':
                return row.invoiceTo?.name || '-';

              case 'userCustomId':
                return row.userCustomId || '-';

              case 'sessionId':
                return row.sessionId || '-';

              case 'transactionId':
                return row.transactionId || '-';

              case 'stationId':
                return row.chargerInfo?.stationId || '-';

              case 'chargerId':
                return row.chargerInfo?.chargerId || '-';

              case 'energyUsed':
                return row.chargingDetails?.energyUsed?.toFixed(3) || '-';

              case 'chargingDuration':
                return row.chargingDetails?.chargingDuration || '-';

              case 'invoiceAmountPaid':
                return row.chargingDetails?.invoiceAmountPaid
                  ? `${ToFixedValue(row.chargingDetails.invoiceAmountPaid)} ${row.currency}`
                  : '-';

              case 'currency':
                return row.currency || '-';

              case 'createdAt':
                return row.createdAt
                  ? `${ConvertUTCtoDate(row.createdAt)} ${convertUtcTo12HourTime(row.createdAt)}`
                  : '-';

              case 'updatedAt':
                return row.updatedAt
                  ? `${ConvertUTCtoDate(row.updatedAt)} ${convertUtcTo12HourTime(row.updatedAt)}`
                  : '-';

              default:
                return '-';
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'invoice-list.csv');
          } else {
            exportPDF(headers, rows, 'invoice-list.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);

        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Pagination
   * @param {any} value page/limit
   */
  const pageLimitChange = (value: any) => {
    const parameters = GetParametersFromCommonFilters(filters);

    fetchData({
      ...parameters,
      ...value,
      sortField,
      sortBy,
    });
  };

  /**
   * Sorting
   * @param {any} sortData sort options
   */
  const handleSort = (sortData: any) => {
    dispatch(setSortData(sortData));

    const parameters = GetParametersFromCommonFilters(filters);

    fetchData({
      page: DefaultApiLimit.page,
      limit,
      ...parameters,
      sortField: sortData.sortField,
      sortBy: sortData.sortBy,
    });
  };

  /**
   * Refresh
   */
  const handleRefresh = () => {
    const parameters = GetParametersFromCommonFilters(filters);

    fetchData({
      page,
      limit,
      ...parameters,
      sortField,
      sortBy,
    });
  };

  /**
   * Function to auto Refresh auto
   */
  //   const handleAutoRefresh = () => {
  //     const queryParameters = GetParametersFromCommonFilters(filters);

  //     dispatch(
  //         refreshAllInvoice({
  //             ...queryParameters,
  //             sortField,
  //             sortBy,
  //             page,
  //             limit,
  //         }),
  //     );
  //   }

  useEffect(() => {
    if (userCustomId) {
      const currentId = userCustomId;
      const lastId = sessionStorage.getItem('invoicesEndConsumer');

      if (isFilterUpdated || invoices.length === 0 || lastId !== currentId) {
        const queryParameters = GetParametersFromCommonFilters(filters);
        fetchData({
          page: DefaultApiLimit.page,
          limit,
          ...queryParameters,
        });
      }
      sessionStorage.setItem('invoicesEndConsumer', currentId);
    } else if (isFilterUpdated || invoices.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
    }
  }, [filters, isFilterUpdated, userCustomId]);

  useEffect(() => {
    if (!isComponent) {
      dispatch(
        setPageTitle({ title: t('invoice'), icon: <TransactionIcon />, count: totalResults }),
      );
    }
  }, [totalResults, t, isComponent, dispatch]);

  return (
    <div className="invoice-list-wrap" dir={i18n.dir()}>
      <CustomTable
        dataKey="InvoiceManagement"
        header={columns}
        data={invoices}
        serialNo
        created
        lastUpdated
        actionButton
        sizeChangeButtonRequired
        version="v2"
        height={isComponent ? 500 : undefined}
        loading={loading}
        page={page}
        limit={limit}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortField, sortBy }}
        handleSortChange={handleSort}
        pageLimitChange={pageLimitChange}
        filters={filters}
        filtersToDispatch={updateInvoiceListFilter}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchInvoiceFilters}
        refreshRequired
        handleRefresh={handleRefresh}
        refershCurrentPages={handleRefresh}
        requiredRefreshToggle
        refreshFunction={handleRefresh}
        refreshInterval={10}
        hoverLabel={t('refresh')}
        leftTopContent={<> </>}
        expandTableOpenKey="InvoiceList"
        exportData={invoices}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        onHiddenColumnsChange={setHiddenColumns}
      />

      <SidePaneModel
        open={isInvoicePanelOpen && Boolean(selectedInvoice)}
        title={t('invoiceDetails', { defaultValue: 'Invoice Details' })}
        width="42vw"
        closePanel={() => {
          setIsInvoicePanelOpen(false);
          setSelectedInvoice(null);
        }}
        content={
          selectedInvoice ? (
            <InvoiceSidePanel
              invoice={selectedInvoice}
              onClose={() => {
                setIsInvoicePanelOpen(false);
                setSelectedInvoice(null);
              }}
            />
          ) : (
            <div />
          )
        }
      />
    </div>
  );
}

export default EmspInvoice;
